import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Loader2, Trash2, GripVertical, Pencil } from "lucide-react";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

const FIELD_TYPES = [
  { v: "text", l: "Short text" },
  { v: "textarea", l: "Paragraph" },
  { v: "number", l: "Number" },
  { v: "date", l: "Date" },
  { v: "select", l: "Dropdown" },
  { v: "multiselect", l: "Multi-select tags" },
  { v: "checkbox", l: "Checkbox (Yes/No)" },
  { v: "rating", l: "Rating" },
  { v: "scale", l: "Scale (numeric)" },
];

const CATEGORIES = ["intake", "wellbeing", "goals", "legal", "supports", "program", "assessment", "general", "other"];

export default function FormTemplates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/form-templates").then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (t) => {
    if (!window.confirm(`Deactivate template "${t.name}"? Existing submissions will be preserved.`)) return;
    try { await api.delete(`/form-templates/${t.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fade-in" data-testid="form-templates-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">CRM</div>
          <h2 className="font-serif text-3xl text-njas-navy">Form Templates</h2>
          <p className="text-sm text-njas-muted mt-1">Design reusable forms your team fills out for each client.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2.5 text-sm hover:bg-njas-navy-hover" data-testid="new-template-btn">
          <Plus className="w-4 h-4" /> New template
        </button>
      </div>

      {loading && <div className="text-njas-muted text-sm">Loading…</div>}
      <div className="grid md:grid-cols-2 gap-3">
        {rows.map((t) => (
          <div key={t.id} className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="eyebrow text-njas-sage-hover text-[10px]">{t.category}</div>
                <div className="font-serif text-xl text-njas-navy">{t.name}</div>
                {t.description && <div className="text-xs text-njas-muted mt-1">{t.description}</div>}
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(t)} className="p-1.5 text-njas-muted hover:text-njas-navy"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(t)} className="p-1.5 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="text-xs text-njas-muted">{t.fields.length} field{t.fields.length === 1 ? "" : "s"}</div>
          </div>
        ))}
      </div>

      {(showNew || editing) && <TemplateEditor initial={editing} onClose={() => { setShowNew(false); setEditing(null); }} onSaved={() => { setShowNew(false); setEditing(null); load(); }} />}
    </div>
  );
}

function TemplateEditor({ initial, onClose, onSaved }) {
  const [f, set] = useState({
    name: initial?.name || "",
    category: initial?.category || "general",
    description: initial?.description || "",
    fields: initial?.fields || [],
    is_active: initial?.is_active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  const addField = () => set({ ...f, fields: [...f.fields, { id: `f${Date.now()}`, label: "", type: "text", required: false, options: [], min: null, max: null }] });
  const updField = (idx, patch) => set({ ...f, fields: f.fields.map((x, i) => i === idx ? { ...x, ...patch } : x) });
  const rmField = (idx) => set({ ...f, fields: f.fields.filter((_, i) => i !== idx) });
  const moveField = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= f.fields.length) return;
    const arr = [...f.fields]; [arr[idx], arr[j]] = [arr[j], arr[idx]]; set({ ...f, fields: arr });
  };

  const save = async (e) => {
    e.preventDefault();
    if (f.fields.length === 0) { toast.error("Add at least one field"); return; }
    setBusy(true);
    try {
      // clean numeric strings
      const cleaned = { ...f, fields: f.fields.map((x) => ({
        ...x,
        min: x.min === "" || x.min == null ? null : Number(x.min),
        max: x.max === "" || x.max == null ? null : Number(x.max),
        options: Array.isArray(x.options) ? x.options : (x.options || "").split(",").map((s) => s.trim()).filter(Boolean),
      }))};
      if (initial?.id) await api.patch(`/form-templates/${initial.id}`, cleaned);
      else await api.post("/form-templates", cleaned);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-serif text-2xl text-njas-navy">{initial?.id ? "Edit template" : "New template"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-njas-muted" /></button>
        </div>
        <form onSubmit={save} className="space-y-4" data-testid="template-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block sm:col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Name</span><input required value={f.name} onChange={upd("name")} className={inputCls} data-testid="tpl-name" /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Category</span>
              <select value={f.category} onChange={upd("category")} className={inputCls}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Description</span><textarea rows={2} value={f.description} onChange={upd("description")} className={inputCls} /></label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-njas-navy">Fields</div>
              <button type="button" onClick={addField} className="inline-flex items-center gap-1 text-sm text-njas-navy hover:underline"><Plus className="w-3.5 h-3.5" /> Add field</button>
            </div>
            <div className="space-y-2">
              {f.fields.length === 0 && <div className="text-sm text-njas-muted">No fields yet. Add one to get started.</div>}
              {f.fields.map((fld, idx) => (
                <div key={fld.id} className="rounded-lg border border-[var(--njas-border)] bg-njas-bg-alt/40 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-njas-muted" />
                    <input value={fld.label} onChange={(e) => updField(idx, { label: e.target.value })} placeholder="Field label" className={`${inputCls} flex-1`} />
                    <select value={fld.type} onChange={(e) => updField(idx, { type: e.target.value })} className={`${inputCls} max-w-[160px]`}>
                      {FIELD_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                    <label className="flex items-center gap-1 text-xs text-njas-muted whitespace-nowrap">
                      <input type="checkbox" checked={fld.required} onChange={(e) => updField(idx, { required: e.target.checked })} /> Required
                    </label>
                    <button type="button" onClick={() => moveField(idx, -1)} className="text-xs text-njas-muted hover:text-njas-navy">↑</button>
                    <button type="button" onClick={() => moveField(idx, 1)} className="text-xs text-njas-muted hover:text-njas-navy">↓</button>
                    <button type="button" onClick={() => rmField(idx)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {(fld.type === "select" || fld.type === "multiselect") && (
                    <input value={Array.isArray(fld.options) ? fld.options.join(", ") : fld.options} onChange={(e) => updField(idx, { options: e.target.value.split(",").map((s) => s.trim()) })} placeholder="Options (comma separated)" className={inputCls} />
                  )}
                  {(fld.type === "number" || fld.type === "rating" || fld.type === "scale") && (
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" value={fld.min ?? ""} onChange={(e) => updField(idx, { min: e.target.value })} placeholder="Min" className={inputCls} />
                      <input type="number" value={fld.max ?? ""} onChange={(e) => updField(idx, { max: e.target.value })} placeholder="Max" className={inputCls} />
                    </div>
                  )}
                  <input value={fld.help_text || ""} onChange={(e) => updField(idx, { help_text: e.target.value })} placeholder="Help text (optional)" className={inputCls} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--njas-border)]">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60" data-testid="tpl-save">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save template
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
